import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL?.trim()
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim()

/**
 * Whether this build has a project to talk to.
 *
 * The template ships without one on purpose: it connects to nothing until a
 * project supplies credentials. Call sites check this before offering anything
 * that needs a session, so an unconfigured build says so instead of failing
 * with a network error nobody can act on.
 */
export const hasSupabaseConfig: boolean = Boolean(url && anonKey)

export const missingSupabaseConfigMessage =
  'This build has no Supabase project configured. Set VITE_SUPABASE_URL and ' +
  'VITE_SUPABASE_ANON_KEY to enable account features.'

// Placeholders so an unconfigured build still mounts. A fresh clone, a CI run,
// and a deploy preview without secrets all have to boot; any call made against
// these fails at the network, which is why call sites guard first.
const PLACEHOLDER_URL = 'https://placeholder.supabase.co'
const PLACEHOLDER_KEY = 'placeholder-anon-key'

/**
 * The browser-side client.
 *
 * The anon key is public by design: it ships in the bundle. What protects the
 * data is row-level security in the database, not the secrecy of this key.
 *
 * Session persistence and refresh are switched off when unconfigured, so a
 * placeholder-keyed entry is never written to storage.
 */
export const supabase = createClient(
  url || PLACEHOLDER_URL,
  anonKey || PLACEHOLDER_KEY,
  {
    // MUSTER's tables live in their own schema, muster, rather than public,
    // so every query needs to say so.
    db: { schema: 'muster' },
    auth: {
      persistSession: hasSupabaseConfig,
      autoRefreshToken: hasSupabaseConfig,
      detectSessionInUrl: hasSupabaseConfig,
    },
  },
)
